import React, { useState, useEffect, useRef } from 'react';
import AppShell from '../components/AppShell';
import { useAuth } from '../context/AuthContext';
import { useChat } from '../context/ChatContext';
import { SegmentedTabs } from '../components/ui/segmented-tabs';
import { cn } from '../lib/utils';
import api from '../lib/api';
import { toast } from 'sonner';
import HeroSection from '../components/home/HeroSection';
import HomePromptBox from '../components/home/HomePromptBox';
import DailyBriefingTab from '../components/home/DailyBriefingTab';
import FeedTab from '../components/home/FeedTab';
import ProjectsTab from '../components/home/ProjectsTab';

const TABS = [
  { id: 'briefing', label: 'Daily briefing' },
  { id: 'feed', label: 'Feed' },
  { id: 'projects', label: 'Projects' },
];

function HomePage() {
  const { user } = useAuth();
  const chat = useChat();

  const [activeTab, setActiveTab] = useState('briefing');
  const [projects, setProjects] = useState([]);
  const [events, setEvents] = useState([]);
  const [briefings, setBriefings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [briefingLoading, setBriefingLoading] = useState(true);
  const [isTabsSticky, setIsTabsSticky] = useState(false);

  const sentinelRef = useRef(null);

  // Fetch projects and events on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [projectsRes, eventsRes] = await Promise.all([
          api.get('/projects'),
          api.get('/events')
        ]);
        setProjects(projectsRes.data);
        setEvents(eventsRes.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load data');
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Fetch stored briefings from database
  useEffect(() => {
    const fetchBriefings = async () => {
      try {
        const res = await api.get('/daily-briefings/latest', { params: { limit: 10 } });
        setBriefings(res.data.briefings || []);
      } catch (error) {
        console.error('Error fetching briefings:', error);
      } finally {
        setBriefingLoading(false);
      }
    };

    fetchBriefings();
  }, []);

  // Sticky tabs with IntersectionObserver
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsTabsSticky(!entry.isIntersecting),
      { threshold: 0 }
    );

    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handlePromptSubmit = ({ query, selectedTools, selectedProjects }) => {
    const projectId = selectedProjects.length > 0 ? selectedProjects[0] : null;
    chat.startNewChat(query, projectId);
  };

  const handleRefreshBriefing = async (projectId) => {
    try {
      setBriefingLoading(true);
      await api.post(`/projects/${projectId}/daily-briefings/generate`);
      const res = await api.get('/daily-briefings/latest', { params: { limit: 10 } });
      setBriefings(res.data.briefings || []);
      toast.success('Briefing refreshed');
    } catch (error) {
      console.error('Error refreshing briefing:', error);
      toast.error('Failed to refresh briefing');
    } finally {
      setBriefingLoading(false);
    }
  };

  const handleStarToggle = async (eventId, starred) => {
    try {
      await api.put(`/events/${eventId}/status`, { starred });
      setEvents(prev => prev.map(e => e.id === eventId ? { ...e, starred } : e));
    } catch (error) {
      console.error('Error updating star:', error);
      toast.error('Failed to update');
    }
  };

  return (
    <AppShell>
      <div className="min-h-screen relative">
        <HeroSection userName={user?.name} />

        <div className="px-10 pb-8 relative z-20">
          <div className="max-w-3xl mx-auto animate-fade-up" style={{ animationDelay: '100ms' }}>
            <HomePromptBox projects={projects} onSubmit={handlePromptSubmit} loading={loading} />
          </div>
        </div>

        <div ref={sentinelRef} />

        <div className={cn(
          'px-10 pb-4 relative z-10 transition-all duration-300',
          isTabsSticky && 'sticky top-0 backdrop-blur-xl bg-white/40 dark:bg-neutral-900/40 pt-3'
        )}>
          <div className="max-w-6xl mx-auto flex justify-center">
            <SegmentedTabs tabs={TABS} activeTab={activeTab} onTabChange={setActiveTab} />
          </div>
        </div>

        <main className="px-10 py-8 pb-20">
          <div className="max-w-6xl mx-auto">
            {activeTab === 'briefing' && (
              <DailyBriefingTab
                briefings={briefings}
                projects={projects}
                loading={briefingLoading}
                onRefresh={handleRefreshBriefing}
              />
            )}
            {activeTab === 'feed' && (
              <FeedTab events={events} projects={projects} onStarToggle={handleStarToggle} loading={loading} />
            )}
            {activeTab === 'projects' && (
              <ProjectsTab projects={projects} loading={loading} />
            )}
          </div>
        </main>
      </div>
    </AppShell>
  );
}

export default HomePage;
